import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Star, Clock, ChevronDown, ChevronUp, ShieldCheck, Heart, X } from "lucide-react";

const STATUS_STYLES = {
  sent: "bg-stone-100 text-stone-600 border-stone-200",
  viewed: "bg-indigo-50 text-indigo-700 border-indigo-200",
  interested: "bg-emerald-50 text-emerald-700 border-emerald-200",
  not_interested: "bg-rose-50 text-rose-600 border-rose-200",
};
const STATUS_LABELS = { sent: "Sent", viewed: "Viewed", interested: "Interested", not_interested: "Not Interested" };

function timeAgo(iso) {
  if (!iso) return "";
  const mins = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function Avatar({ name, photo }) {
  if (photo) return <img src={photo} alt={name} className="w-11 h-11 rounded-full object-cover border border-stone-200 shrink-0" />;
  const initials = (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="w-11 h-11 rounded-full grid place-items-center text-sm font-medium text-amber-800 bg-amber-100 border border-amber-200 shrink-0">
      {initials}
    </div>
  );
}

function TimelineList({ timeline }) {
  return (
    <div className="mt-3 border-t border-stone-100 pt-3 space-y-2" data-testid="rec-timeline">
      <div className="text-[10px] uppercase tracking-widest text-stone-400">Interaction timeline</div>
      {(timeline || []).map((ev, i) => (
        <div key={`${ev.at}-${i}`} className="flex items-start gap-2 text-xs text-stone-600">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 shrink-0" />
          <div>
            <span className="font-medium text-[#1E1B4B] capitalize">{(ev.type || "").replace("_", " ")}</span>
            <span className="text-stone-400"> · {new Date(ev.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>
            {ev.note && <div className="text-stone-500">{ev.note}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

function RecCard({ rec, features, onRespond, onViewed }) {
  const [open, setOpen] = useState(false);
  const responded = ["interested", "not_interested"].includes(rec.status);
  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && rec.status === "sent") onViewed(rec.id);
  };
  return (
    <div className={`border rounded-xl p-4 bg-white ${rec.priority ? "border-amber-300" : "border-stone-200"}`} data-testid={`rec-card-${rec.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <Avatar name={rec.candidate_name} photo={rec.candidate?.photo_url} />
          <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-serif-display text-lg text-[#1E1B4B]" data-testid={`rec-name-${rec.id}`}>{rec.candidate_first_name || rec.candidate_name}</span>
            {rec.priority && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300 rounded-full px-2 py-0.5" data-testid="priority-badge">
                <Star size={10} /> Priority Match
              </span>
            )}
            {rec.candidate?.kyc_status === "verified" && (
              <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700"><ShieldCheck size={11} /> Verified</span>
            )}
          </div>
          <div className="text-xs text-stone-500 mt-1">
            {[rec.candidate?.profession, rec.candidate?.city].filter(Boolean).join(" · ")}
          </div>
          <div className="text-sm mt-1.5">
            <span className="text-amber-700 font-semibold">{rec.overall_score}%</span>
            <span className="text-stone-500 text-xs"> compatibility · Lifestyle {rec.lv_score}% · Professional {rec.pe_score}%</span>
          </div>
          {rec.their_status === "interested" && (
            <div className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1 mt-2" data-testid={`rec-their-status-${rec.id}`}>
              <Heart size={11} /> They&apos;re interested in you!
            </div>
          )}
          {rec.their_status === "not_interested" && (
            <div className="inline-flex items-center gap-1 text-xs text-stone-500 bg-stone-50 border border-stone-200 rounded-full px-2.5 py-1 mt-2" data-testid={`rec-their-status-${rec.id}`}>
              They passed for now
            </div>
          )}
          {features?.last_active && rec.last_active && (
            <div className="inline-flex items-center gap-1 text-xs text-stone-500 mt-1.5 ml-1" data-testid="rec-last-active">
              <Clock size={11} /> Last active {timeAgo(rec.last_active)}
            </div>
          )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className={`text-[11px] border rounded-full px-2.5 py-0.5 ${STATUS_STYLES[rec.status] || STATUS_STYLES.sent}`} data-testid={`rec-status-${rec.id}`}>
            {STATUS_LABELS[rec.status] || rec.status}
          </span>
          <span className="text-[10px] text-stone-400">Week of {rec.week_of}</span>
        </div>
      </div>
      {!responded && (
        <div className="mt-3 flex gap-2">
          <button
            data-testid={`rec-interested-btn-${rec.id}`}
            onClick={() => onRespond(rec.id, "interested")}
            className="btn-pill flex-1 inline-flex items-center justify-center gap-1.5 text-xs text-white py-2" style={{ background: "#1E1B4B" }}>
            <Heart size={13} /> I&apos;m Interested
          </button>
          <button
            data-testid={`rec-not-interested-btn-${rec.id}`}
            onClick={() => onRespond(rec.id, "not_interested")}
            className="btn-pill flex-1 inline-flex items-center justify-center gap-1.5 text-xs text-stone-600 border border-stone-300 py-2 hover:bg-stone-50">
            <X size={13} /> Not My Type
          </button>
        </div>
      )}
      {features?.timeline && (
        <button onClick={toggle} data-testid={`rec-timeline-toggle-${rec.id}`}
          className="mt-3 inline-flex items-center gap-1 text-xs text-amber-700">
          {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />} {open ? "Hide" : "View"} interaction timeline
        </button>
      )}
      {!features?.timeline && rec.status === "sent" && (
        <button onClick={() => onViewed(rec.id)} data-testid={`rec-view-btn-${rec.id}`}
          className="mt-3 inline-flex items-center gap-1 text-xs text-amber-700">
          Mark as viewed
        </button>
      )}
      {open && features?.timeline && <TimelineList timeline={rec.timeline} />}
    </div>
  );
}

export const RecommendationsPanel = () => {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/recommendations");
      setData(d);
      setError(false);
    } catch {
      setError(true);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    load();
    const poll = setInterval(load, 15000);
    return () => clearInterval(poll);
  }, [load]);

  const respond = async (id, response) => {
    try {
      await api.post(`/recommendations/${id}/respond`, { response });
      toast.success(response === "interested" ? "Noted! We've let them know someone is interested." : "Got it — we'll refine future matches.");
      load();
    } catch { toast.error("Could not record your response"); }
  };
  const viewed = async (id) => {
    try { await api.post(`/recommendations/${id}/viewed`); load(); } catch { /* noop */ }
  };

  const recs = data?.recommendations || [];
  return (
    <div className="border border-stone-200 rounded-2xl bg-white p-6" data-testid="my-recommendations-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="overline">My Recommendations</div>
          <p className="text-xs text-stone-500 mt-1">Curated every Monday at 9 AM IST · 65% Lifestyle &amp; Values · 35% Professional</p>
        </div>
        <Sparkles size={18} className="text-amber-600" />
      </div>
      {!loaded ? (
        <div className="mt-5 space-y-3" data-testid="rec-loading">
          {[0, 1].map((i) => (
            <div key={i} className="border border-stone-200 rounded-xl p-4 animate-pulse flex gap-3">
              <div className="w-11 h-11 rounded-full bg-stone-100 shrink-0" />
              <div className="flex-1 space-y-2 py-1">
                <div className="h-3 w-1/3 bg-stone-100 rounded" />
                <div className="h-3 w-1/2 bg-stone-100 rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="mt-5 text-sm text-stone-500 bg-[#F9F8F6] border border-stone-200 rounded-xl p-5" data-testid="rec-error-state">
          We couldn&apos;t load your recommendations just now. They&apos;ll appear here shortly — please refresh in a moment.
        </div>
      ) : recs.length === 0 ? (
        <div className="mt-5 text-sm text-stone-500 bg-[#F9F8F6] border border-stone-200 rounded-xl p-5" data-testid="rec-empty-state">
          Your first weekly matches arrive this Monday at 9 AM IST. Complete your assessment and profile so the engine can work its magic.
        </div>
      ) : (
        <div className="mt-5 space-y-3">
          {recs.map((r) => (
            <RecCard key={r.id} rec={r} features={data.features} onRespond={respond} onViewed={viewed} />
          ))}
        </div>
      )}
    </div>
  );
};
